import { useEffect, useRef, useState } from 'react'
import styles from './ProposedSkillDialog.module.css'

/**
 * ProposedSkillDialog — confirm/cancel overlay for approving or rejecting a candidate
 * skill. Richer than the generic ConfirmDialog: the approve variant lets the approver
 * pick the skill's classification, and the reject variant captures a reason.
 *
 * Props:
 *   mode      {'approve'|'reject'} — which variant to render
 *   skillName {string}            — the candidate skill's name (shown in the copy)
 *   types     {string[]}          — valid skill types for the approve dropdown (from the
 *                                   page's backend-loaded list, so new types appear here)
 *   busy      {boolean}           — disables the confirm button while a call is inflight
 *   onConfirm {function}          — called with the decision payload:
 *                                   approve → { skill_type }, reject → { reason }
 *   onCancel  {function}
 */
export default function ProposedSkillDialog({ mode, skillName, types = [], busy = false, onConfirm, onCancel }) {
  const isApprove = mode === 'approve'
  // The page loads the types before opening the dialog, so the list is present at mount.
  // Keep 'Technical' as the preferred default when available, else fall back to the first.
  const [skillType, setSkillType] = useState(() => (types.includes('Technical') ? 'Technical' : (types[0] ?? '')))
  const [reason, setReason] = useState('')
  const firstFieldRef = useRef(null)

  // Autofocus the first field. The dialog is intentionally non-dismissable except via
  // the Cancel button — no Escape-to-close and no backdrop-click-to-close — so a
  // decision is never abandoned by an accidental click or keystroke.
  useEffect(() => {
    firstFieldRef.current?.focus()
  }, [])

  function handleConfirm() {
    if (busy) return
    onConfirm(
      isApprove
        ? { skill_type: skillType }
        : { reason: reason.trim() || null },
    )
  }

  return (
    <div className={styles.backdrop}>
      <div className={styles.dialog} role="dialog" aria-modal="true"
        aria-label={isApprove ? 'Approve skill' : 'Reject skill'}>

        <div className={styles.header}>
          <span className={`${styles.eyebrow} ${isApprove ? '' : styles.eyebrowDanger}`}>
            {isApprove ? 'Approve Skill' : 'Reject Skill'}
          </span>
          <h2 className={styles.skillName}>{skillName}</h2>
        </div>

        {isApprove ? (
          <>
            <p className={styles.message}>
              GaussIQ will begin tracking this skill coverage and proposing it for team
              members.
            </p>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Skill Type</span>
              <select
                ref={firstFieldRef}
                className={styles.select}
                value={skillType}
                onChange={e => setSkillType(e.target.value)}
              >
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          </>
        ) : (
          <>
            <p className={styles.message}>
              GaussIQ will stop proposing this skill and won’t track its coverage.
            </p>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Enter Rejection Reason</span>
              <textarea
                ref={firstFieldRef}
                className={styles.textarea}
                rows={3}
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Optional — e.g. Duplicate of an existing skill"
              />
            </label>
          </>
        )}

        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className={`${styles.confirmBtn} ${isApprove ? '' : styles.danger}`}
            onClick={handleConfirm}
            disabled={busy}>
            {isApprove ? 'Approve' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  )
}
